import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, Sparkles, Camera, Zap, ArrowRight, Star } from "lucide-react";

import { useAuth } from "@/auth/AuthProvider";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";

type OnboardingStatus = {
  bootstrapped: boolean;
  needsSetup: boolean;
};

export function LandingPage() {
  const { user, loading, claim } = useAuth();
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatus | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  useEffect(() => {
    apiFetch<OnboardingStatus>("/api/onboarding/status")
      .then(setOnboardingStatus)
      .catch(() =>
        setOnboardingStatus({ bootstrapped: true, needsSetup: false }),
      )
      .finally(() => setCheckingOnboarding(false));
  }, []);

  // Show loader while checking
  if (checkingOnboarding || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Redirect to onboarding if system needs setup
  if (onboardingStatus?.needsSetup) {
    return <Navigate to="/onboarding" replace />;
  }

  // Redirect authenticated users to their dashboard
  if (user && claim) {
    if (claim.role === "kioskconsumer") return <Navigate to="/kiosk" replace />;
    if (claim.role === "imagepackcreator")
      return <Navigate to="/creator" replace />;
    if (claim.role === "admin") return <Navigate to="/admin" replace />;
    // Fallback to home if role not recognized
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-slate-50 selection:bg-purple-500/30">
      {/* Abstract Background Elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] left-0 h-[1000px] w-[1000px] -translate-x-1/2 rounded-full bg-purple-900/20 blur-[100px]" />
        <div className="absolute top-[20%] right-0 h-[800px] w-[800px] translate-x-1/3 rounded-full bg-indigo-900/20 blur-[100px]" />
        <div className="absolute bottom-0 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-pink-900/10 blur-[100px]" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="pointer-events-none absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150"></div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      {/* Header */}
      <header className="fixed top-0 z-50 w-full border-b border-white/5 bg-slate-950/50 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-tr from-purple-600 to-indigo-600 shadow-lg shadow-purple-500/20">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white">
              Memorix
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="hidden text-slate-400 hover:text-white sm:inline-flex"
            >
              <Link to="/login">Sign In</Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="bg-white text-slate-950 hover:bg-slate-200"
            >
              <Link to="/login">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center pt-32 pb-20 lg:pt-48">
        {/* Hero Section */}
        <div className="container px-6 text-center">
          <div className="animate-fade-in-up mx-auto mb-6 flex w-fit items-center justify-center rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-1.5 backdrop-blur-md">
            <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-purple-300">
              <Star className="h-3 w-3 fill-purple-300" />
              New Generation of Souvenirs
            </span>
          </div>

          <h1 className="animate-fade-in-up mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/50 sm:text-7xl lg:text-8xl [text-wrap:balance]">
            Turn Memories <br className="hidden sm:block" />
            into{" "}
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent">
              Magic
            </span>
          </h1>

          <p className="animate-fade-in-up delay-100 mx-auto mt-8 max-w-2xl text-lg text-slate-400 sm:text-xl [text-wrap:pretty]">
            The AI-powered kiosk platform that transforms venue experiences into
            digital masterpieces. Guests choose a scene, strike a pose, and take
            home a piece of art.
          </p>

          <div className="animate-fade-in-up delay-200 mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="h-14 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-8 text-lg font-semibold shadow-lg shadow-purple-500/25 transition-all hover:scale-105 hover:shadow-purple-500/40"
            >
              <Link to="/login">
                Start Creating <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-14 rounded-full border-slate-700 bg-slate-900/50 px-8 text-lg text-slate-300 backdrop-blur-sm hover:bg-slate-800 hover:text-white"
            >
              <Link to="/login">Partner Login</Link>
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="container mt-32 px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Feature 1 */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 transition-all hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/10">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-500/10 text-purple-400 ring-1 ring-purple-500/20 transition-all group-hover:scale-110 group-hover:bg-purple-500 group-hover:text-white group-hover:ring-0">
                <Camera className="h-7 w-7" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">
                Kiosk Experience
              </h3>
              <p className="text-slate-400 leading-relaxed">
                A seamless, touch-friendly flow designed for guests. Capture
                selfies, pick immersive scenes, and generate memories in
                seconds.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 transition-all hover:border-pink-500/50 hover:shadow-2xl hover:shadow-pink-500/10">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-400 ring-1 ring-pink-500/20 transition-all group-hover:scale-110 group-hover:bg-pink-500 group-hover:text-white group-hover:ring-0">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">
                AI Generation
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Powered by state-of-the-art AI models. We transform simple
                photos into stylized artwork and cinematic videos instantly.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 transition-all hover:border-amber-500/50 hover:shadow-2xl hover:shadow-amber-500/10">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20 transition-all group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white group-hover:ring-0">
                <Zap className="h-7 w-7" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white">
                Instant Delivery
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Zero friction. Guests scan a QR code to download their
                masterpieces immediately. No waiting, just sharing.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 bg-slate-950 py-12">
        <div className="container mx-auto flex flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-white/10">
              <Sparkles className="h-3 w-3 text-white/70" />
            </div>
            <p className="text-sm font-medium text-slate-400">
              Memorix &copy; {new Date().getFullYear()}
            </p>
          </div>
          <div className="flex gap-8 text-sm text-slate-500">
            <a href="#" className="hover:text-white transition-colors">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
